import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { Child } from "../types";
import VoxelBackground from "../components/VoxelBackground";

export default function Dashboard() {
  const [children, setChildren] = useState<Child[]>([]);
  const [showAddChild, setShowAddChild] = useState(false);
  const [childName, setChildName] = useState("");
  const [childAge, setChildAge] = useState("");
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const parent = JSON.parse(localStorage.getItem("parent") || "{}");

  useEffect(() => {
    fetchChildren();
  }, []);

  const fetchChildren = async () => {
    try {
      const res = await api.get("/children");
      setChildren(res.data.children);
    } catch {
      navigate("/login");
    } finally {
      setLoading(false);
    }
  };

  const addChild = async () => {
    try {
      await api.post("/children", {
        name: childName,
        age: parseInt(childAge),
        avatar: "robot1",
      });
      setChildName("");
      setChildAge("");
      setShowAddChild(false);
      fetchChildren();
    } catch (err) {
      alert("Błąd dodawania dziecka");
    }
  };

  const logout = () => {
    localStorage.clear();
    navigate("/login");
  };

  if (loading) return <div style={styles.loading}>⏳ Ładowanie...</div>;

  return (
    <div style={styles.container}>
      <VoxelBackground />
      <div style={styles.header}>
        <h1 style={styles.logo}>🧮 Mathix</h1>
        <div style={styles.headerRight}>
          <span style={styles.welcome}>Cześć, {parent.name}! 👋</span>
          <button onClick={logout} style={styles.logoutBtn}>Wyloguj</button>
        </div>
      </div>

      <div style={styles.content}>
        <h2 style={styles.title}>Twoje dzieci</h2>

        <div style={styles.childrenGrid}>
          {children.map((child) => (
            <div key={child.id} style={styles.childCard}>
              <div style={styles.avatar}>🤖</div>
              <h3 style={styles.childName}>{child.name}</h3>
              <p style={styles.childInfo}>Wiek: {child.age} lat</p>
              <div style={styles.pointsBadge}>⭐ {child.points} pkt</div>
              <div style={styles.levelBadge}>Poziom {child.level}</div>
              <button
                style={styles.playBtn}
                onClick={() => navigate(`/child/${child.id}`)}
              >
                Graj teraz! 🚀
              </button>
              <button
                style={styles.progressBtn}
                onClick={() => navigate(`/progress/${child.id}`)}
              >
                📊 Postępy
              </button>
            </div>
          ))}

          <div style={styles.addCard} onClick={() => setShowAddChild(true)}>
            <div style={styles.addIcon}>+</div>
            <p style={styles.addText}>Dodaj dziecko</p>
          </div>
        </div>

        {showAddChild && (
          <div style={styles.modal}>
            <div style={styles.modalBox}>
              <h3>Dodaj dziecko 👶</h3>
              <input
                style={styles.input}
                placeholder="Imię dziecka"
                value={childName}
                onChange={(e) => setChildName(e.target.value)}
              />
              <input
                style={styles.input}
                placeholder="Wiek"
                type="number"
                value={childAge}
                onChange={(e) => setChildAge(e.target.value)}
              />
              <div style={styles.modalButtons}>
                <button style={styles.cancelBtn} onClick={() => setShowAddChild(false)}>
                  Anuluj
                </button>
                <button style={styles.confirmBtn} onClick={addChild}>
                  Dodaj! 🎉
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100vh",
    background: "#87CEEB",
    position: "relative",
    overflow: "hidden",
  },
  header: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    padding: "16px 32px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
    zIndex: 2,
  },
  logo: { color: "white", margin: 0, fontSize: "28px" },
  headerRight: { display: "flex", alignItems: "center", gap: "16px" },
  welcome: { color: "white", fontSize: "16px" },
  logoutBtn: {
    background: "rgba(255,255,255,0.2)",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "8px 16px",
    cursor: "pointer",
  },
  content: {
    padding: "32px",
    position: "relative",
    zIndex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  title: {
    fontSize: "28px",
    marginBottom: "32px",
    color: "#333",
    textShadow: "0 2px 4px rgba(255,255,255,0.8)",
  },
  childrenGrid: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: "24px",
    justifyContent: "center",
    maxWidth: "1000px",
  },
  childCard: {
    background: "white",
    borderRadius: "24px",
    padding: "32px 24px",
    textAlign: "center",
    width: "220px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
  },
  avatar: { fontSize: "64px", marginBottom: "12px" },
  childName: { margin: "0 0 8px", fontSize: "22px", color: "#333" },
  childInfo: { color: "#888", margin: "0 0 12px" },
  pointsBadge: {
    background: "#fff3cd",
    borderRadius: "20px",
    padding: "4px 12px",
    display: "inline-block",
    marginBottom: "8px",
    fontSize: "14px",
  },
  levelBadge: {
    background: "#e8f5e9",
    borderRadius: "20px",
    padding: "4px 12px",
    display: "inline-block",
    marginBottom: "16px",
    fontSize: "14px",
    color: "#2e7d32",
  },
  playBtn: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white",
    border: "none",
    borderRadius: "12px",
    padding: "10px 20px",
    cursor: "pointer",
    width: "100%",
    fontSize: "16px",
    marginBottom: "8px",
  },
  progressBtn: {
    background: "white",
    color: "#667eea",
    border: "2px solid #667eea",
    borderRadius: "12px",
    padding: "10px 20px",
    cursor: "pointer",
    width: "100%",
    fontSize: "16px",
  },
  addCard: {
    background: "rgba(255,255,255,0.8)",
    borderRadius: "24px",
    padding: "32px 24px",
    textAlign: "center",
    cursor: "pointer",
    border: "2px dashed #ccc",
    width: "220px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "200px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
  },
  addIcon: { fontSize: "48px", color: "#ccc", marginBottom: "8px" },
  addText: { color: "#888", fontSize: "16px" },
  modal: {
    position: "fixed",
    top: 0, left: 0, right: 0, bottom: 0,
    background: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
  },
  modalBox: {
    background: "white",
    borderRadius: "20px",
    padding: "32px",
    width: "100%",
    maxWidth: "400px",
    textAlign: "center",
  },
  input: {
    width: "100%",
    padding: "14px",
    marginBottom: "16px",
    borderRadius: "12px",
    border: "2px solid #eee",
    fontSize: "16px",
    boxSizing: "border-box" as const,
  },
  modalButtons: { display: "flex", gap: "12px" },
  cancelBtn: {
    flex: 1, padding: "12px", borderRadius: "12px",
    border: "2px solid #eee", background: "white",
    cursor: "pointer", fontSize: "16px",
  },
  confirmBtn: {
    flex: 1, padding: "12px", borderRadius: "12px",
    border: "none",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    color: "white", cursor: "pointer", fontSize: "16px",
  },
  loading: {
    display: "flex", alignItems: "center", justifyContent: "center",
    minHeight: "100vh", fontSize: "24px",
  },
};